// These fallback labels apply only after configured cashflow buckets miss.
// Provider account/report classifications are still the source model, but
// provider/accountant row labels can vary and stable item codes may be
// compact provider keys. Check both the code and display label, and keep this
// matcher narrow and exception-backed instead of adding customer-specific names.
// IAS 7 frames direct-method cashflow as gross cash receipts/payments; IRD
// confirms paid-out GST refunds are cash receipts to the bank account.
// See docs/guides/ACCOUNTING_STANDARDS.md,
// docs/app/readmes/methodology/CASHFLOW_BREAKDOWN.md,
// https://www.ifrs.org/issued-standards/list-of-standards/ias-7-statement-of-cash-flows/,
// https://www.ird.govt.nz/gst/filing-and-paying-gst-and-refunds/gst-refunds,
// https://developer.xero.com/documentation/api/accounting/accounts,
// https://developer.xero.com/documentation/api/accounting/reports, and
// https://developer.intuit.com/app/developer/qbo/docs/workflows/run-reports.
const SYNTHETIC_OUTFLOW_WORD_TOKENS = new Set([
  "gst",
  "vat",
  "hst",
  "pst",
  "tax",
  "paye",
  "payg",
  "payable",
  "payroll",
]);

const SYNTHETIC_OUTFLOW_WORD_PREFIXES = ["withhold", "liabilit"] as const;

const CASH_RECEIPT_EXCEPTION_WORD_TOKENS = new Set([
  "refund",
  "refunds",
  "refunded",
  "refunding",
  "rebate",
  "rebates",
  "rebated",
  "rebating",
]);

const SYNTHETIC_INFLOW_EXCEPTION_WORD_TOKENS = new Set([
  ...CASH_RECEIPT_EXCEPTION_WORD_TOKENS,
  "credit",
  "receivable",
  "recoverable",
]);

const SYNTHETIC_OUTFLOW_COMPACT_PHRASES = [
  "gsttopay",
  "gstpayable",
  "vatpayable",
  "hstpayable",
  "pstpayable",
  "payepayable",
  "paygpayable",
  "wagespayable",
  "wagepayable",
  "incometax",
  "salestax",
  "taxpayable",
  "taxliability",
  "taxestopay",
  "withholdingpayable",
  "payrollliability",
] as const;

export const toCashflowLabelWords = (label: string): string[] =>
  label
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const hasCashReceiptExceptionLabel = (label: string): boolean =>
  toCashflowLabelWords(label).some((word) =>
    CASH_RECEIPT_EXCEPTION_WORD_TOKENS.has(word),
  );

export const hasCashReceiptExceptionForItem = (
  itemCode: string,
  itemLabelsByCode: Record<string, string | null | undefined> | undefined,
): boolean => {
  const itemLabel = itemLabelsByCode?.[itemCode];
  return [itemCode, itemLabel].some(
    (candidate) =>
      typeof candidate === "string" && hasCashReceiptExceptionLabel(candidate),
  );
};

export const hasSyntheticOutflowLabel = (
  codeWords: string[],
  normalisedCode: string,
): boolean => {
  const hasOutflowWord = codeWords.some(
    (word) =>
      SYNTHETIC_OUTFLOW_WORD_TOKENS.has(word) ||
      SYNTHETIC_OUTFLOW_WORD_PREFIXES.some((prefix) => word.startsWith(prefix)),
  );
  const hasOutflowCompactPhrase = SYNTHETIC_OUTFLOW_COMPACT_PHRASES.some(
    (phrase) => normalisedCode.includes(phrase),
  );

  return hasOutflowWord || hasOutflowCompactPhrase;
};

export const hasSyntheticInflowExceptionLabel = (
  codeWords: string[],
): boolean =>
  codeWords.some((word) => SYNTHETIC_INFLOW_EXCEPTION_WORD_TOKENS.has(word));

export const hasCashReceiptExceptionWords = (codeWords: string[]): boolean =>
  codeWords.some((word) => CASH_RECEIPT_EXCEPTION_WORD_TOKENS.has(word));
